// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, type FocusEvent, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import {
  type BendCreatorValidationRequest,
  bendInputKeys,
  type BendValidationResponse,
  extractErrorMessage,
  isCanceledApiRequest,
  type StrongExclude,
  useEvolvePost,
} from '@evolvemep/api-ts';
import type { GetInputPropsOptions, GetInputPropsReturnType } from '@mantine/form';
import { useTimeout } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';

import { isAlmostExactlyEqual } from 'helpers/isAlmostExactlyEqual';
import { isKeyOf } from 'helpers/isKeyOf';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { strongEntries } from 'helpers/strongEntries';
import { useGeneralContext } from 'helpers/useGeneralContext';
import {
  type BendCreatorFormValues,
  bendInputDefaults,
  useBendCreatorFormContext,
} from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { getBestKnownBendRadius } from 'modules/Shop/EvolveFabrication/BendCreator/State/bendRadius';
import { canCenterBend, solveCenterBend } from 'modules/Shop/EvolveFabrication/BendCreator/State/centerBend';
import { useConduits } from 'modules/Shop/EvolveFabrication/useConduits';

type ValidatableKey = StrongExclude<keyof BendCreatorFormValues, 'partName' | 'quantity'>;

/** How long after the last change to wait before validating, so quick stepper taps are not each validated. In ms. */
const VALIDATION_DEBOUNCE_MS = 400;

type BendCreatorValidationContextValue = {
  /**
   * Validates the bend once changes pause for a moment. Cancels any validation already in flight, so a response for
   * older values never overwrites newer ones. Call after the form's values have been updated.
   */
  scheduleValidation: (
    /** Optional. If provided, indicates what field changed to trigger validation. */
    changingKey?: ValidatableKey,
  ) => void;
  /** Validates the bend with the backend straight away. */
  validateBend: <T extends StrongExclude<keyof BendCreatorFormValues, 'partName' | 'quantity'>>(
    /** Optional. If provided, indicates what field changed to trigger validation. */
    changingKey?: T,
    /** Optional. if provided, indicates the new value of the field that changed to trigger validation. */
    changingValue?: BendCreatorFormValues[T],
  ) => Promise<boolean>;
  /** Wrapper around getInputProps that performs serverside validation. */
  wrappedGetInputProps: <T extends StrongExclude<keyof BendCreatorFormValues, 'partName' | 'quantity'>>(
    path: T,
    options?: GetInputPropsOptions & {
      /** When true, the input will perform backend validation on change. If unset or false, the input will validate on blur. */
      validateOnChange?: boolean;
    },
  ) => GetInputPropsReturnType;
  /** True while a validation is scheduled or in flight. */
  isValidating: boolean;
  /** True only while a validation request is in flight, not while one is waiting to be sent. */
  isRequestInFlight: boolean;
  lastValidation: LastValidationState | null;
  /** Resets the validation state to null/not yet validated. Generally, if you did something that would invalidate the form (like blanking a field), you should call this. */
  resetLastValidation: () => void;
  /**
   * Replaces the validation state with one saved earlier, cancelling any scheduled or in-flight validation.
   * Use it when loading saved values into the form, so they don't need validating again.
   */
  restoreLastValidation: (validation: LastValidationState | null) => void;
};

const BendCreatorValidationContextProvider = createContext<BendCreatorValidationContextValue | undefined>(undefined);

/**
 * This represents a backend error with a dictionary of field names to error messages.
 * If we get this kind of error we can use the values to set field errors on the form.
 */
type ServiceValidationError = {
  title: string;
  status: number;
  errors: Record<keyof BendCreatorValidationRequest, string[]>;
  traceId: string;
};

export type LastValidationState = {
  request: BendCreatorValidationRequest;
} & (
  | {
      response: BendValidationResponse;
      isSuccess: true;
    }
  | {
      response: unknown;
      isSuccess: false;
    }
);

/**
 * If we have an HTTP400 error, assume it's a ServiceValidationError.
 */
const isServiceValidationError = (e: unknown): e is { response: { data: ServiceValidationError } } => {
  if (isNil(e)) return false;
  if (typeof e !== 'object') return false;
  return 'status' in e && e.status === 400;
};

export const BendCreatorValidationContext = ({
  children,
  validateOnMount,
}: {
  children: ReactNode;
  validateOnMount?: boolean;
}) => {
  const form = useBendCreatorFormContext();
  const {
    apiCall: requestValidation,
    loading: isValidating,
    abortController,
  } = useEvolvePost('shop/fabrication/bendCreator/validate', {
    alertOnError: false,
  });

  const [lastValidation, setLastValidation] = useState<LastValidationState | null>(null);
  const { values, getValues, setFieldValue, setFieldError, clearFieldError, getInputProps, isValid, validateField } =
    form;
  const { conduitSizes } = useConduits();

  const prepareValues = useCallback(
    <T extends StrongExclude<keyof BendCreatorFormValues, 'partName' | 'quantity'>>(
      changingKey?: T,
      changingValue?: BendCreatorFormValues[T],
    ): BendCreatorValidationRequest | null => {
      const {
        partName: _,
        quantity: __,
        benderProfile,
        conduitTypeId,
        conduitSizeId,
        bendType,
        materialStockLength,
        centerBend,
        ...rest
      } = values;

      /* Don't try to perform server side validation until
       * required fields are present, some bend dimension value isn't blank, and the user's input is valid clientside. */
      if (
        isNil(benderProfile) ||
        isNil(conduitTypeId) ||
        isNil(conduitSizeId) ||
        isNil(bendType) ||
        isNil(materialStockLength)
      ) {
        return null;
      }
      if (!bendInputKeys.some((key) => isNotNil(values[key]))) return null;
      if (bendInputKeys.some((key) => !isValid(key))) return null;

      /* Some special case handling for angled bends, which can be overdefined.
       * Treat the possibly overdefined value that the user didn't touch as null.
       * It will be updated post-validation */
      if (bendType === 'Angled') {
        if (changingKey === 'kickHeight') {
          rest.centerToEnd1 = null;
        } else {
          rest.kickHeight = null;
        }
      }

      return {
        ...rest,
        benderProfileId: benderProfile.machineProfileId,
        conduitTypeId,
        conduitSizeId,
        bendType,
        materialStockLength,
        // Center Bend is a maximized leg with the obstruction placed to make the end straights equal.
        maximizeLegLength: rest.maximizeLegLength || centerBend,
        /** If the changing key and value are provided, use them instead of what's in `rest`, which could be stale. */
        ...(isNotNil(changingKey) && isNotNil(changingValue) ? { [changingKey]: changingValue } : {}),
      };
    },
    [values, isValid],
  );

  // On success, update the form with values from the backend
  const handleSuccess = useCallback(
    (res: Awaited<ReturnType<typeof requestValidation>>) => {
      clearFieldError('bendType');
      res.updatedBend.forEach(({ keyName, value }) => {
        setFieldValue(keyName, value);
        clearFieldError(keyName);
      });
    },
    [setFieldValue, clearFieldError],
  );

  // On error, if possible, update the form with errors, otherwise show a notification.
  const handleError = useCallback(
    (e: unknown) => {
      // Ignore abort errors.
      if (isCanceledApiRequest(e)) return;
      if (isServiceValidationError(e)) {
        const {
          response: {
            data: { errors },
          },
        } = e;
        // Put any errors that are not related to a given field on the "bend type" field.
        const generalErrors: string[] = [];
        strongEntries(errors).forEach(([key, errorMessages]) => {
          if (isKeyOf(key, values)) {
            if (key === 'bendType') {
              generalErrors.push(...errorMessages);
            } else {
              setFieldError(key, errorMessages.join(', '));
            }
          } else {
            generalErrors.push(...errorMessages);
          }
        });
        if (generalErrors.length > 0) {
          setFieldError('bendType', generalErrors.join(', '));
        }
      } else {
        notifications.show({
          title: 'Error validating bend',
          message: `An unexpected error occurred while validating bend: ${extractErrorMessage(e)}`,
          color: 'red',
        });
      }
    },
    [values, setFieldError],
  );

  /**
   * After a Center Bend validation, the obstruction position that the server's take up length gives, if it
   * differs from the position that was sent. Turns Center Bend off if the server's take up length is too short.
   */
  const getCenterBendCorrection = useCallback(
    (request: BendCreatorValidationRequest, response: BendValidationResponse) => {
      if (!getValues().centerBend || !canCenterBend(request.bendType)) return null;
      const takeUpLength = response.updatedBend.find(({ keyName }) => keyName === 'takeUpLength')?.value;
      if (isNil(takeUpLength)) return null;
      const solution = solveCenterBend(request, response.bendRadius, takeUpLength);
      if (solution.status === 'tooLong') setFieldValue('centerBend', false);
      if (solution.status !== 'fits') return null;
      const { position } = solution;
      const sentPosition = request[position.keyName];
      if (isNotNil(sentPosition) && isAlmostExactlyEqual(sentPosition, position.value, 1e-9)) return null;
      return position;
    },
    [getValues, setFieldValue],
  );

  /**
   * With Center Bend on, sets the two fields it owns from the form's current values, estimating the take up
   * length with the best bend radius known. Turns Center Bend off when the bend is too long for the stick.
   */
  const applyCenterBend = useCallback(() => {
    const current = getValues();
    if (!current.centerBend) return;
    const solution = solveCenterBend(current, getBestKnownBendRadius(lastValidation, current, conduitSizes));
    if (solution.status === 'tooLong') {
      setFieldValue('centerBend', false);
      return;
    }
    if (solution.status !== 'fits') return;
    setFieldValue('takeUpLength', solution.takeUpLength);
    setFieldValue(solution.position.keyName, solution.position.value);
    clearFieldError('takeUpLength');
    clearFieldError(solution.position.keyName);
  }, [getValues, lastValidation, conduitSizes, setFieldValue, clearFieldError]);

  /**
   * Validate the bend and update the form with the results.
   * Returns true indicating that an error occurred, or false indicating the validation was successful.
   */
  const validateBend = useCallback(
    async <T extends StrongExclude<keyof BendCreatorFormValues, 'partName' | 'quantity'>>(
      changingKey?: T,
      changingValue?: BendCreatorFormValues[T],
    ) => {
      const validationValues = prepareValues(changingKey, changingValue);
      if (isNil(validationValues)) {
        // The bend can't be validated as it stands, so an earlier success no longer describes it.
        setLastValidation(null);
        return true;
      }

      abortController.current.abort('Newer validation request made');
      // oxlint-disable-next-line react/immutability
      abortController.current = new AbortController();

      try {
        let request = validationValues;
        let res = await requestValidation(request);
        handleSuccess(res);
        // With Center Bend, the obstruction position was solved from an estimated take up length. If the
        // server's take up length moves it, validate once more at the corrected position.
        const correction = getCenterBendCorrection(request, res);
        if (isNotNil(correction)) {
          setFieldValue(correction.keyName, correction.value);
          request = { ...request, [correction.keyName]: correction.value };
          res = await requestValidation(request);
          handleSuccess(res);
        }
        setLastValidation({ request, response: res, isSuccess: true });
        return false;
      } catch (e) {
        // A cancelled request was superseded by newer values or a restored state, so it says nothing about the bend.
        if (isCanceledApiRequest(e)) return true;
        handleError(e);
        setLastValidation({ request: validationValues, response: e, isSuccess: false });
        return true;
      }
    },
    [
      abortController,
      requestValidation,
      prepareValues,
      handleSuccess,
      handleError,
      getCenterBendCorrection,
      setFieldValue,
    ],
  );

  // The debounced validation runs after the form has re-rendered with the new values, so it reads the latest
  // validateBend from a ref rather than the one captured when the change was scheduled.
  const latestValidateBend = useRef(validateBend);
  useEffect(() => {
    latestValidateBend.current = validateBend;
  }, [validateBend]);
  const pendingChangingKey = useRef<ValidatableKey | undefined>(undefined);
  const [validationScheduled, setValidationScheduled] = useState(false);
  const { start: startValidationTimer, clear: clearValidationTimer } = useTimeout(() => {
    setValidationScheduled(false);
    void latestValidateBend.current(pendingChangingKey.current);
    pendingChangingKey.current = undefined;
  }, VALIDATION_DEBOUNCE_MS);

  const scheduleValidation = useCallback(
    (changingKey?: ValidatableKey) => {
      applyCenterBend();
      abortController.current.abort('Newer validation request scheduled');
      pendingChangingKey.current = changingKey ?? pendingChangingKey.current;
      setValidationScheduled(true);
      clearValidationTimer();
      startValidationTimer();
    },
    [applyCenterBend, abortController, clearValidationTimer, startValidationTimer],
  );

  const wrappedGetInputProps = useCallback(
    <T extends StrongExclude<keyof BendCreatorFormValues, 'partName' | 'quantity'>>(
      path: T,
      options?: GetInputPropsOptions & {
        /** When true, the input will perform backend validation on change. If unset or false, the input will validate on blur. */
        validateOnChange?: boolean;
      },
    ) => {
      const { onBlur: internalOnBlur, onChange: internalOnChange, ...rest } = getInputProps(path, options);
      const onBlur = (e: FocusEvent<HTMLInputElement>) => {
        // mantine use-form's typing upsets me.
        // oxlint-disable-next-line typescript/no-unsafe-call
        internalOnBlur?.(e);
        if (options?.validateOnChange) return;
        /* Surface the clientside message for a bad bend dimension, which `validateBend` won't send to the backend.
         * Only when the value is invalid: `validateField` clears the field's error when its rule passes,
         * which would wipe an error the backend set. */
        if (isKeyOf(path, bendInputDefaults) && !isValid(path)) {
          validateField(path);
        }
        scheduleValidation(path);
      };
      const onChange = (newValue: BendCreatorFormValues[T] | null) => {
        // oxlint-disable-next-line typescript/no-unsafe-call
        internalOnChange?.(newValue);
        if (options?.validateOnChange) {
          scheduleValidation(path);
        }
      };

      return { ...rest, onBlur, onChange };
    },
    [scheduleValidation, getInputProps, isValid, validateField],
  );

  const resetLastValidation = useCallback(() => {
    setLastValidation(null);
  }, []);

  const restoreLastValidation = useCallback(
    (validation: LastValidationState | null) => {
      clearValidationTimer();
      setValidationScheduled(false);
      pendingChangingKey.current = undefined;
      abortController.current.abort('Validation state restored');
      setLastValidation(validation);
    },
    [clearValidationTimer, abortController],
  );

  const shouldDoInitialValidation = useRef(validateOnMount ?? false);
  useEffect(() => {
    if (shouldDoInitialValidation.current) {
      void validateBend();
      shouldDoInitialValidation.current = false;
    }
  }, [validateBend]);

  const value = {
    scheduleValidation,
    validateBend,
    wrappedGetInputProps,
    isValidating: isValidating || validationScheduled,
    isRequestInFlight: isValidating,
    lastValidation,
    resetLastValidation,
    restoreLastValidation,
  };
  return <BendCreatorValidationContextProvider value={value}>{children}</BendCreatorValidationContextProvider>;
};

export const useBendCreatorValidationContext = () =>
  useGeneralContext(BendCreatorValidationContextProvider, 'BendCreatorValidationContext');
